import { useMemo } from "react";
import type { CampaignSummaryWithStats } from "@/components/features/campaigns/types";
import { CAMPAIGN_VOLUME_CONSTANTS } from "@/constants/trading/campaigns";
import { useDevice } from "@/hooks/ui/useDevice";
import { getTargetVolume } from "@/utils/campaign/volume";

// Format large numbers with K/M suffixes
export const formatValue = (value: number): string => {
  if (value >= 1000000) {
    return (value / 1000000).toPrecision(3) + "m";
  } else if (value >= 1000) {
    return (value / 1000).toPrecision(3) + "k";
  } else {
    return value.toPrecision(3);
  }
};

interface VolumeData {
  currentVolume: number;
  targetVolume: number | null;
  formattedCurrentVolume: string;
  formattedTargetVolume: string | null;
  progress: number;
  isHighProgress: boolean;
}

interface UseCampaignVolumeDataProps {
  campaign: CampaignSummaryWithStats;
  showProgressBar?: boolean;
  forceShowOnMobileTablet?: boolean;
}

export const useCampaignVolumeData = ({
  campaign,
  showProgressBar = true,
  forceShowOnMobileTablet = false,
}: UseCampaignVolumeDataProps) => {
  const { isMobile, isTablet } = useDevice();

  const volumeData: VolumeData = useMemo(() => {
    const currentVolume = campaign.currentVolume || 0;
    const targetVolume = getTargetVolume(campaign);
    const formattedCurrentVolume = formatValue(currentVolume);
    const formattedTargetVolume = targetVolume ? formatValue(targetVolume) : null;
    const progress = targetVolume ? Math.min((currentVolume / targetVolume) * 100, 100) : 0;
    const isHighProgress = progress >= CAMPAIGN_VOLUME_CONSTANTS.HIGH_PROGRESS_THRESHOLD;

    return {
      currentVolume,
      targetVolume,
      formattedCurrentVolume,
      formattedTargetVolume,
      progress,
      isHighProgress,
    };
  }, [campaign]);

  const shouldShowProgressBar = useMemo(() => {
    // If forceShowOnMobileTablet is true, ignore mobile/tablet check
    if (forceShowOnMobileTablet) {
      return volumeData.targetVolume && showProgressBar;
    }
    // Otherwise, apply the default behavior (hide on mobile/tablet)
    return volumeData.targetVolume && !isMobile && !isTablet && showProgressBar;
  }, [volumeData.targetVolume, isMobile, isTablet, showProgressBar, forceShowOnMobileTablet]);

  return {
    volumeData,
    shouldShowProgressBar,
  };
};
